import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Handshake, Wifi, MapPin, BadgeCheck, Search } from "lucide-react";
import { toast } from "sonner";

/**
 * Collaborations feed — like the Jobs board but for "let's team up" posts.
 * Titles are rendered bold so users can scan at a glance.
 */
export default function Collabs() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState({ categories: [], work_types: [] });
  const [cat, setCat] = useState("");
  const [wt, setWt] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/collabs/categories").then(r => setMeta(r.data || {})).catch(()=>{});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (cat) params.set("category", cat);
    if (wt)  params.set("work_type", wt);
    if (q)   params.set("q", q);
    api.get(`/collabs?${params.toString()}`)
      .then(r => setItems(r.data || []))
      .catch(e => toast.error(formatErr(e)));
  }, [cat, wt, q]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="collabs-page">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <p className="overline">Collaborations</p>
          <h1 className="h-display text-4xl">Team up.</h1>
          <p className="text-[#4A4A4A] mt-1">Find Hoomans looking for a co-founder, co-artist, co-organiser — you name it.</p>
        </div>
        {user && (
          <Link to="/collabs/new" className="btn-primary" data-testid="collabs-new-btn"><Plus size={14}/> Post a collab</Link>
        )}
      </div>

      <div className="mt-6 grid sm:grid-cols-[1fr_auto_auto] gap-2 items-center" data-testid="collabs-filters">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A] pointer-events-none"/>
          <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search titles & descriptions…"
                 className="input !pl-9" data-testid="collabs-search"/>
        </div>
        <select className="input" value={cat} onChange={(e)=>setCat(e.target.value)} data-testid="collabs-category">
          <option value="">All categories</option>
          {meta.categories?.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="flex gap-1" data-testid="collabs-worktype">
          {[["", "Any"], ["remote", "Remote"], ["in_person", "In-person"], ["hybrid", "Hybrid"]].map(([v,l]) => (
            <button key={v || "any"} onClick={()=>setWt(v)}
              data-testid={`collabs-wt-${v || "any"}`}
              className={`text-xs px-3 py-2 rounded-full ${wt===v ? "bg-[#0A0A0A] text-white" : "border border-[#E5E5E3] text-[#4A4A4A] hover:bg-[#F2F2F0]"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {items === null && <p className="text-[#8A8A8A] mt-8">Loading…</p>}
      {items && items.length === 0 && (
        <div className="card p-12 text-center mt-8" data-testid="collabs-empty">
          <Handshake size={26} className="mx-auto text-[#0057FF] mb-2"/>
          <p className="text-[#4A4A4A]">No collaborations yet. Be the first to post one.</p>
          {user && <Link to="/collabs/new" className="btn-secondary inline-flex mt-3">Post a collab</Link>}
        </div>
      )}
      <ul className="space-y-3 mt-6" data-testid="collabs-list">
        {(items || []).map(c => <CollabRow key={c.id} c={c}/>)}
      </ul>
    </div>
  );
}

function CollabRow({ c }) {
  return (
    <li className="card p-5 hover:border-[#0A0A0A] transition-colors" data-testid={`collab-row-${c.id}`}>
      <Link to={`/collabs/${c.id}`} className="block">
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            {/* Title is bold and prominent for easy scanning */}
            <p className="text-lg font-bold text-[#0A0A0A] leading-snug" data-testid={`collab-title-${c.id}`}>{c.title}</p>
            <p className="text-xs text-[#8A8A8A] mt-1 inline-flex items-center gap-2 flex-wrap">
              <span className="badge-soft">{c.category}</span>
              <span className="inline-flex items-center gap-1">
                {c.work_type === "remote" ? <><Wifi size={11}/> Remote</>
                  : c.work_type === "hybrid" ? "Hybrid"
                  : <><MapPin size={11}/> In-person</>}
              </span>
              {c.location && <span>· {c.location}</span>}
              <span>· {new Date(c.created_at).toLocaleDateString()}</span>
            </p>
            <p className="text-sm text-[#4A4A4A] mt-2 line-clamp-2">{c.description}</p>
            <div className="flex items-center gap-2 mt-3">
              {c.owner?.avatar && <img src={c.owner.avatar} alt="" className="h-6 w-6 rounded-full object-cover"/>}
              <p className="text-xs text-[#4A4A4A] inline-flex items-center gap-1">
                by <span className="font-medium">{c.owner?.name}</span>
                {c.owner?.verified && <BadgeCheck size={11} className="text-[#0057FF]"/>}
              </p>
            </div>
          </div>
        </div>
      </Link>
    </li>
  );
}
